import { Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { hasApiErrorCode, useApiErrorMessage } from "@/shared/api";
import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";

import { billingStatusQueryOptions } from "../../api/billingStatusQueryOptions";
import { useBillingStatus } from "../../api/useBillingStatus";
import { useCreateCheckout } from "../../api/useCreateCheckout";
import { usePayment } from "../../api/usePayment";
import { formatProDate } from "../../lib/formatProDate";
import { goToPaymentPage } from "../../lib/goToPaymentPage";
import { useProPlanLabels } from "../../lib/useProPlanLabels";
import { CheckoutChoose } from "./CheckoutChoose";
import { CheckoutState } from "./CheckoutState";
import { checkoutBody } from "./CheckoutWay";

type CheckoutDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Платёж, с которого игрок вернулся со страницы оплаты (`/pricing?payment=…`): окно сразу
  // показывает его статус.
  returnedPaymentId: string | null;
};

// Окно оплаты Pro по макету design/prototype/ProCheckout.dc.html: выбор способа и согласие →
// «Готовим оплату…» (платёж создаётся в фоне) → переход на страницу платёжного сервиса →
// возврат на `/pricing?payment=…` → «Проверяем оплату…» → готово или «не прошла». Пока оплата
// выключена (`enabled: false`), — «Оплата скоро».
export const CheckoutDialog = ({ open, onOpenChange, returnedPaymentId }: CheckoutDialogProps) => {
  const { i18n } = useLingui();
  const { price, period } = useProPlanLabels();
  const status = useBillingStatus(open);
  const checkout = useCreateCheckout();
  const errorMessage = useApiErrorMessage(checkout.error);
  // Платёж, начатый в этом окне: как только у него появится ссылка — переходим на неё.
  const startedId = checkout.data?.id ?? null;
  // «Попробовать снова» после возврата со страницы оплаты: старый платёж больше не показываем.
  const [returnDismissed, setReturnDismissed] = useState(false);
  const paymentId = startedId ?? (returnDismissed ? null : returnedPaymentId);
  const payment = usePayment(open ? paymentId : null);

  const paymentUrl = payment.data?.payment_url ?? null;
  const redirectTo =
    startedId !== null && payment.data?.status === "awaiting_payment" ? paymentUrl : null;
  useEffect(() => {
    if (redirectTo !== null) {
      goToPaymentPage(redirectTo);
    }
  }, [redirectTo]);
  // Оплата подтверждена — статус Pro (до какого числа, следующее списание) перечитывается и без
  // WebSocket: событие `billing.updated` могло не дойти.
  const queryClient = useQueryClient();
  const confirmed = payment.data?.status === "confirmed";
  useEffect(() => {
    if (confirmed) {
      void queryClient.invalidateQueries({ queryKey: billingStatusQueryOptions().queryKey });
    }
  }, [confirmed, queryClient]);

  const restart = () => {
    setReturnDismissed(true);
    checkout.reset();
  };

  const disabled =
    status.data?.enabled === false || hasApiErrorCode(checkout.error, "billing_disabled");

  const body = (() => {
    if (disabled) {
      return (
        <CheckoutState
          tone="soon"
          title={<Trans>Payments are coming soon</Trans>}
          text={
            <Trans>
              We are connecting the payment service. As soon as it works, you can get Pro here.
            </Trans>
          }
        />
      );
    }
    if (paymentId === null) {
      return (
        <CheckoutChoose
          pending={checkout.isPending}
          error={
            errorMessage !== null && (
              <p role="alert" className="text-sm text-bad">
                {errorMessage}
              </p>
            )
          }
          onSubmit={(way) => {
            checkout.checkout(checkoutBody(way));
          }}
        />
      );
    }
    const current = payment.data;
    if (current === undefined || current.status === "creating" || redirectTo !== null) {
      return (
        <CheckoutState
          tone="wait"
          title={<Trans>Preparing payment…</Trans>}
          text={<Trans>The payment service page will open in a moment.</Trans>}
        />
      );
    }
    if (current.status === "awaiting_payment") {
      return (
        <CheckoutState
          tone="wait"
          title={<Trans>Checking the payment…</Trans>}
          text={
            <Trans>
              It usually takes a few seconds. If you paid via SBP, confirm the payment in your bank
              app.
            </Trans>
          }
        >
          {paymentUrl !== null && (
            <Button asChild variant="outline">
              <a href={paymentUrl} className="no-underline">
                <Trans>Open the payment page again</Trans>
              </a>
            </Button>
          )}
        </CheckoutState>
      );
    }
    if (current.status === "confirmed") {
      const until =
        current.pro_until === null ? null : formatProDate(current.pro_until, i18n.locale);
      const nextChargeAt = status.data?.autopay?.next_charge_at ?? null;
      const nextCharge = nextChargeAt === null ? null : formatProDate(nextChargeAt, i18n.locale);
      return (
        <CheckoutState
          tone="ok"
          title={until === null ? <Trans>Pro is on</Trans> : <Trans>Pro until {until}</Trans>}
          text={
            current.mode !== "autopay" ? (
              <Trans>You can extend it on the Plans page.</Trans>
            ) : nextCharge === null ? (
              <Trans>Autopay is on.</Trans>
            ) : (
              <Trans>
                Autopay is on: the next {price} will be charged on {nextCharge}.
              </Trans>
            )
          }
        >
          <Button
            size="lg"
            className="font-semibold"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            <Trans>Done</Trans>
          </Button>
        </CheckoutState>
      );
    }
    return (
      <CheckoutState
        tone="fail"
        title={<Trans>Payment didn't go through</Trans>}
        text={<Trans>No money was charged. If it was, we will refund it — write to support.</Trans>}
      >
        <Button size="lg" className="font-semibold" onClick={restart}>
          <Trans context="payment">Try again</Trans>
        </Button>
      </CheckoutState>
    );
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          restart();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-[520px] gap-4.5 sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="pr-8 font-display text-[28px] leading-tight font-normal">
            <Trans>
              Pro for {period} — {price}
            </Trans>
          </DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
};
